import { ArrowLeft, Rocket, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { cn } from "@/lib/utils";

// UFO Icon Component
function UFOSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      {/* UFO Body */}
      <ellipse cx="50" cy="60" rx="35" ry="12" fill="currentColor" opacity="0.3" />
      <ellipse cx="50" cy="55" rx="30" ry="8" fill="currentColor" />
      <ellipse cx="50" cy="50" rx="25" ry="6" fill="currentColor" opacity="0.8" />

      {/* UFO Dome */}
      <ellipse cx="50" cy="45" rx="20" ry="15" fill="currentColor" opacity="0.6" />

      {/* Lights */}
      <circle cx="40" cy="55" r="2" fill="currentColor" className="animate-pulse" />
      <circle
        cx="50"
        cy="55"
        r="2"
        fill="currentColor"
        className="animate-pulse"
        style={{ animationDelay: '0.2s' }}
      />
      <circle
        cx="60"
        cy="55"
        r="2"
        fill="currentColor"
        className="animate-pulse"
        style={{ animationDelay: '0.4s' }}
      />

      {/* Beam */}
      <ellipse cx="50" cy="70" rx="15" ry="8" fill="currentColor" opacity="0.2" />
    </svg>
  );
}

// Alien Icon Component
function AlienSvg({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      {/* Head */}
      <ellipse cx="50" cy="35" rx="25" ry="30" fill="currentColor" />

      {/* Eyes */}
      <circle cx="42" cy="30" r="6" fill="white" />
      <circle cx="58" cy="30" r="6" fill="white" />
      <circle cx="42" cy="30" r="3" fill="currentColor" />
      <circle cx="58" cy="30" r="3" fill="currentColor" />

      {/* Mouth */}
      <ellipse cx="50" cy="42" rx="8" ry="4" fill="white" opacity="0.8" />

      {/* Antenna */}
      <line x1="50" y1="10" x2="50" y2="20" stroke="currentColor" strokeWidth="2" />
      <circle cx="50" cy="8" r="3" fill="currentColor" />

      {/* Body */}
      <ellipse cx="50" cy="65" rx="15" ry="20" fill="currentColor" opacity="0.8" />

      {/* Arms */}
      <ellipse cx="30" cy="60" rx="8" ry="15" fill="currentColor" />
      <ellipse cx="70" cy="60" rx="8" ry="15" fill="currentColor" />

      {/* Legs */}
      <ellipse cx="40" cy="85" rx="6" ry="12" fill="currentColor" />
      <ellipse cx="60" cy="85" rx="6" ry="12" fill="currentColor" />
    </svg>
  );
}

interface NotFoundProps {
  type?: 'protected' | 'public';
}

function NotFound({ type = 'protected' }: NotFoundProps) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div
      className={cn('flex items-center justify-center p-4', {
        'h-full': type === 'protected',
        'min-h-screen': type === 'public',
      })}
    >
      <Empty className="relative max-w-2xl">
        {/* Space background effect */}
        <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-lg">
          {/* Top corners - safe on mobile */}
          <Sparkles className="text-primary/40 absolute left-2 top-2 h-4 w-4 animate-pulse sm:left-4 sm:top-4 sm:h-6 sm:w-6" />
          <Sparkles
            className="text-primary/30 absolute right-2 top-2 h-4 w-4 animate-pulse sm:right-8 sm:top-12 sm:h-5 sm:w-5"
            style={{ animationDelay: '0.3s' }}
          />
          {/* Bottom corners - safe on mobile */}
          <Sparkles
            className="text-primary/35 absolute bottom-2 left-2 h-4 w-4 animate-pulse sm:bottom-8 sm:left-12 sm:h-4 sm:w-4"
            style={{ animationDelay: '0.6s' }}
          />
          <Sparkles
            className="text-primary/40 absolute bottom-2 right-2 h-4 w-4 animate-pulse sm:bottom-16 sm:right-4 sm:h-5 sm:w-5"
            style={{ animationDelay: '0.9s' }}
          />
          {/* Top edge center - safe on mobile */}
          <Sparkles
            className="text-primary/30 absolute left-1/2 top-1 h-4 w-4 -translate-x-1/2 animate-pulse sm:left-1/2 sm:top-8 sm:h-4 sm:w-4"
            style={{ animationDelay: '1.2s' }}
          />
          {/* Side edges - safe on mobile */}
          <Sparkles
            className="text-primary/35 absolute right-1 top-1/4 h-4 w-4 animate-pulse sm:right-1/4 sm:top-1/4 sm:h-5 sm:w-5"
            style={{ animationDelay: '1.5s' }}
          />
          {/* Bottom edge - safe on mobile */}
          <Sparkles
            className="text-primary/30 absolute bottom-1 right-2 h-4 w-4 animate-pulse sm:right-12 sm:top-1/2 sm:h-6 sm:w-6"
            style={{ animationDelay: '2.1s' }}
          />
          <Sparkles
            className="text-primary/35 absolute bottom-1/4 left-2 h-4 w-4 animate-pulse sm:bottom-1/4 sm:left-8 sm:h-5 sm:w-5"
            style={{ animationDelay: '2.4s' }}
          />
        </div>

        <EmptyHeader className="relative z-10">
          <EmptyMedia
            variant="icon"
            className="relative mb-4 !size-auto min-h-[100px] min-w-[100px] sm:mb-6 sm:min-h-[140px] sm:min-w-[140px] md:min-h-[180px] md:min-w-[180px]"
          >
            <div className="relative flex items-center justify-center">
              <UFOSvg className="text-primary !h-24 !w-24 sm:!h-32 sm:!w-32 md:!h-40 md:!w-40" />
              <AlienSvg className="text-primary/60 absolute -bottom-4 -right-4 !h-12 !w-12 sm:!h-16 sm:!w-16 md:!h-20 md:!w-20" />
            </div>
          </EmptyMedia>
          <EmptyTitle className="relative z-10 text-3xl sm:text-4xl md:text-5xl">
            404
            <br />
            Lost in Space
          </EmptyTitle>
          <EmptyDescription className="relative z-10 text-base sm:text-lg md:text-xl">
            This page has been abducted by aliens! It doesn't exist in this dimension.
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent className="relative z-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
            <Button
              onClick={handleGoBack}
              variant="outline"
              className="box-border h-11 w-full text-base sm:w-auto sm:min-w-[140px] sm:text-lg"
              size="lg"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Go Back
            </Button>
            <Button
              onClick={handleGoHome}
              className="box-border h-11 w-full text-base sm:w-auto sm:min-w-[140px] sm:text-lg"
              size="lg"
            >
              <Rocket className="mr-2 h-5 w-5" />
              Return to Base
            </Button>
          </div>
        </EmptyContent>
      </Empty>
    </div>
  );
}

export default NotFound;
